import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TextareaModule } from 'primeng/textarea';

export interface ImportedPlayer {
  name: string;
  /** Sur l'échelle canonique 0-100, ou null si absent du texte collé. */
  rating: number | null;
}

/** Puces et numérotations en tête de ligne: "1.", "2)", "- ", "* ", "• ", "> ". */
const PREFIX = /^\s*(?:\d{1,3}\s*[.)\-–]\s*|[-*•>·]\s*)/;
/** Niveau en fin de ligne: "Nom - 4", "Nom : 80", "Nom (3)", "Nom | 72". */
const TRAILING_RATING = /^(.*\S)\s*(?:[-–:|]\s*|\(\s*)(\d{1,3})\s*\)?$/;

@Component({
  selector: 'app-import-dialog',
  standalone: true,
  imports: [FormsModule, TranslateModule, ButtonModule, DialogModule, TextareaModule],
  template: `
    <p-dialog
      [header]="'IMPORT_MODAL_TITLE' | translate"
      [visible]="visible"
      (visibleChange)="onVisibleChange($event)"
      [modal]="true"
      [style]="{ width: '92vw', maxWidth: '560px' }"
      [draggable]="false"
      [resizable]="false"
      [dismissableMask]="true"
    >
      <div class="import">
        <p class="import__intro">{{ 'IMPORT_INTRO' | translate }}</p>

        <label class="sr-only" for="importText">{{ 'IMPORT_MODAL_TITLE' | translate }}</label>
        <textarea
          pTextarea
          id="importText"
          rows="8"
          class="import__area"
          [placeholder]="'IMPORT_PLACEHOLDER' | translate"
          [ngModel]="text()"
          (ngModelChange)="text.set($event)"
        ></textarea>

        <div class="import__hint">
          <code>Karim</code>
          <code>Sofiane - 4</code>
          <code>Lucas : 78</code>
          <code>Ana, Bea, Cleo</code>
        </div>
        <p class="import__note">{{ 'IMPORT_RATING_HINT' | translate }}</p>

        @if (parsed().length > 0) {
          <p class="import__preview" role="status">
            <i class="pi pi-check-circle" aria-hidden="true"></i>
            {{ 'IMPORT_DETECTED' | translate: { count: parsed().length } }}
            <span class="import__names">{{ previewNames() }}</span>
          </p>
        }
      </div>

      <ng-template pTemplate="footer">
        <div class="import__footer">
          <button pButton class="p-button-text p-button-secondary" (click)="onVisibleChange(false)">
            {{ 'CANCEL' | translate }}
          </button>
          <button pButton icon="pi pi-plus" (click)="confirm()" [disabled]="parsed().length === 0">
            {{ 'CONFIRM_IMPORT' | translate }}
          </button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [
    `
      .import {
        display: flex;
        flex-direction: column;
        gap: var(--ef-s-3);
      }

      .import__intro {
        margin: 0;
        font-size: 0.9375rem;
        color: var(--ef-text-muted);
      }

      .import__area {
        width: 100%;
        min-height: 160px;
        padding: var(--ef-s-3);
        border: 1px solid var(--ef-border);
        border-radius: var(--ef-r-sm);
        background: var(--ef-surface);
        color: var(--ef-text);
        font: inherit;
        font-size: 1rem;
        line-height: 1.6;
        resize: vertical;
      }

      .import__area:hover {
        border-color: var(--ef-border-strong);
      }

      .import__area:focus {
        border-color: var(--ef-primary);
        box-shadow: 0 0 0 3px var(--ef-ring);
        outline: none;
      }

      .import__hint {
        display: flex;
        flex-wrap: wrap;
        gap: var(--ef-s-2);
      }

      .import__hint code {
        padding: 0.1875rem 0.5rem;
        border-radius: var(--ef-r-xs);
        background: var(--ef-surface-3);
        border: 1px solid var(--ef-border);
        font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
        font-size: 0.75rem;
        color: var(--ef-text-muted);
      }

      .import__note {
        margin: 0;
        font-size: 0.8125rem;
        color: var(--ef-text-faint);
      }

      .import__preview {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 0.375rem;
        margin: 0;
        padding: var(--ef-s-2) var(--ef-s-3);
        border-radius: var(--ef-r-sm);
        background: var(--ef-primary-soft);
        font-size: 0.8125rem;
        font-weight: 600;
        color: var(--ef-text);
      }

      .import__preview i {
        color: var(--ef-primary);
      }

      .import__names {
        flex: 1;
        min-width: 0;
        font-weight: 400;
        color: var(--ef-text-muted);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .import__footer {
        display: flex;
        justify-content: flex-end;
        gap: var(--ef-s-2);
      }
    `
  ]
})
export class ImportDialogComponent {
  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() playersImported = new EventEmitter<ImportedPlayer[]>();

  readonly text = signal('');

  readonly parsed = computed(() => this.parse(this.text()));

  readonly previewNames = computed(() =>
    this.parsed()
      .map((p) => p.name)
      .join(', ')
  );

  onVisibleChange(value: boolean) {
    this.visible = value;
    this.visibleChange.emit(value);
    if (!value) this.text.set('');
  }

  confirm() {
    const players = this.parsed();
    if (players.length === 0) return;
    this.playersImported.emit(players);
    this.onVisibleChange(false);
  }

  /**
   * Une entrée par ligne (ou par point-virgule). Une ligne sans niveau mais
   * contenant des virgules est éclatée en plusieurs joueurs, ce qui couvre le
   * copier-coller « Ana, Bea, Cleo » d'un groupe de discussion.
   */
  private parse(raw: string): ImportedPlayer[] {
    if (!raw.trim()) return [];

    const players: ImportedPlayer[] = [];

    for (const chunk of raw.split(/[\n;]+/)) {
      const line = chunk.replace(PREFIX, '').trim();
      if (!line) continue;

      const match = TRAILING_RATING.exec(line);
      if (match) {
        const name = match[1].trim();
        const value = Number.parseInt(match[2], 10);
        if (name) {
          players.push({ name, rating: this.toCanonical(value) });
          continue;
        }
      }

      if (line.includes(',')) {
        for (const part of line.split(',')) {
          const name = part.replace(PREFIX, '').trim();
          if (name) players.push({ name, rating: null });
        }
        continue;
      }

      players.push({ name: line, rating: null });
    }

    return players;
  }

  /** 1-5 est interprété comme des étoiles, 6-100 comme un score direct. */
  private toCanonical(value: number): number | null {
    if (value >= 1 && value <= 5) return value * 20;
    if (value > 5 && value <= 100) return value;
    return null;
  }
}
